<template>
  <div v-if="chips.length" class="chips" aria-label="Active filters">
    <LgChip
      v-for="chip in chips"
      :key="chipKey(chip)"
      :tone="toneFor(chip)"
      :clickable="!chip.negatable"
      removable
      :title="chip.negatable ? undefined : `Remove filter ${chip.label}`"
      :remove-label="`Remove filter ${chip.label}`"
      :class="{ 'is-exclude': chip.negate }"
      @click="!chip.negatable && $emit('remove', chip)"
      @remove="$emit('remove', chip)"
    >
      <!-- Negatable (text/attr) chips get an include/exclude toggle. Because the
           toggle is itself a button, the chip body is a plain span here (not a
           clickable button) so we never nest a button inside a button. -->
      <button
        v-if="chip.negatable"
        type="button"
        class="negate-toggle"
        :aria-label="`${chip.negate ? 'Include' : 'Exclude'} filter ${chip.label}`"
        :title="chip.negate ? 'Excluding — click to include' : 'Including — click to exclude'"
        @click.stop="$emit('toggle-negate', chip)"
      >
        {{ chip.negate ? '≠' : '=' }}
      </button>
      <span class="chip-label">{{ chip.label }}</span>
    </LgChip>

    <LgButton size="sm" variant="ghost" class="clear-btn" @click="$emit('clear')">
      Clear all
    </LgButton>
  </div>
</template>

<script setup lang="ts">
import type { Chip } from '../composables/useFilters.js';
import LgChip from '../ui/LgChip.vue';
import LgButton from '../ui/LgButton.vue';

defineProps<{ chips: Chip[] }>();
defineEmits<{ remove: [chip: Chip]; clear: []; 'toggle-negate': [chip: Chip] }>();

type Tone = 'accent' | 'info' | 'debug' | 'warn' | 'neutral' | 'danger';

/** Excluded (negated) text/attr chips read as `danger` (red edge) so they're
 * visually distinct from includes while keeping full-contrast text (WCAG AA);
 * the `≠` toggle reinforces it. Includes and non-negatable chips keep their
 * per-kind tone. */
function toneFor(chip: Chip): Tone {
  if (chip.negate) return 'danger';
  switch (chip.kind) {
    case 'text':
      return 'accent';
    case 'container':
      return 'info';
    case 'stack':
      return 'debug';
    case 'level':
      return 'warn';
    case 'stream':
      return 'accent';
    default:
      return 'neutral';
  }
}

/** Stable v-for key — attr chips need key+value+negate to stay distinct. */
function chipKey(chip: Chip): string {
  return `${chip.kind}:${chip.key ?? ''}:${chip.value}:${chip.negate ? '1' : '0'}`;
}
</script>

<style scoped>
.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-2) var(--lg-space-4);
  border-bottom: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
}
.clear-btn {
  border-style: dashed;
}

/* Include/exclude toggle sits at the leading edge of a text/attr chip. */
.negate-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-right: var(--lg-space-1);
  padding: 0;
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text-dim);
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  line-height: 1;
  cursor: pointer;
}
.negate-toggle:hover {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.negate-toggle:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}
.is-exclude .negate-toggle {
  color: var(--lg-danger);
}
.chip-label {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Excluded terms read as struck-through-ish via the danger tone + the ≠ glyph;
   keep the label itself full contrast so it stays readable. */
.is-exclude .chip-label {
  text-decoration: line-through;
  text-decoration-color: var(--lg-danger);
  text-decoration-thickness: 1px;
}

/* Mobile: single scrollable row instead of wrapping. */
@media (max-width: 768px) {
  .chips {
    flex-wrap: nowrap;
    width: max-content;
    min-width: 100%;
    padding: var(--lg-space-2) var(--lg-space-3);
  }
}
</style>
